'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { ChatMessageAvatar } from './chat-message-avatar';

export type ChatIdentityRole = 'operator' | 'stakeholder' | 'external' | 'system';

export type ChatMessageIdentityProps = {
  name: string;
  role: ChatIdentityRole;
  initials: string;
  avatarImageUrl?: string;
  title?: string;
  organization?: string;
  handle?: string;
  isStacked?: boolean;
};

type RoleTokens = {
  label: string;
  badge: string;
  badgeTone: string;
};

const roleTokenMap: Record<ChatIdentityRole, RoleTokens> = {
  operator: {
    label: 'User',
    badge: 'OP',
    badgeTone:
      'bg-[color-mix(in_oklab,var(--token-foreground-default)_75%,transparent)] text-[var(--token-surface-default)]',
  },
  stakeholder: {
    label: 'Stakeholder',
    badge: 'EX',
    badgeTone: 'bg-[color-mix(in_oklab,var(--token-warning-default)_65%,transparent)] text-foreground',
  },
  external: {
    label: 'External',
    badge: 'EX',
    badgeTone: 'bg-[color-mix(in_oklab,var(--token-warning-default)_65%,transparent)] text-foreground',
  },
  system: {
    label: 'Scirix',
    badge: 'S',
    badgeTone: 'bg-[color-mix(in_oklab,var(--token-info-default)_70%,transparent)] text-foreground',
  },
};

export function ChatMessageIdentity({
  name,
  role,
  initials,
  avatarImageUrl,
  title,
  organization,
  handle,
  isStacked,
}: ChatMessageIdentityProps) {
  const [calloutOpen, setCalloutOpen] = useState(false);
  const hoverTimeoutRef = useRef<number | null>(null);
  const roleTokens = roleTokenMap[role];
  const tooltipText = `${name} · ${roleTokens.label}`;

  const clearHoverTimer = () => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const scheduleCallout = () => {
    clearHoverTimer();
    hoverTimeoutRef.current = window.setTimeout(() => setCalloutOpen(true), 650);
  };

  const closeCallout = () => {
    clearHoverTimer();
    setCalloutOpen(false);
  };

  useEffect(() => () => clearHoverTimer(), []);

  return (
    <div className="flex w-14 flex-col items-center">
      <Popover open={calloutOpen} onOpenChange={setCalloutOpen}>
        <PopoverTrigger asChild>
          <div
            role="button"
            tabIndex={0}
            className={cn(
              'relative flex flex-col items-center outline-none',
              isStacked && 'opacity-60',
            )}
            title={tooltipText}
            aria-label={tooltipText}
            onPointerEnter={scheduleCallout}
            onPointerLeave={closeCallout}
            onFocus={scheduleCallout}
            onBlur={closeCallout}
            onPointerDown={(event) => event.preventDefault()}
          >
            <ChatMessageAvatar
              initials={initials}
              variant={
                role === 'system'
                  ? 'system'
                  : role === 'operator'
                    ? 'operator'
                    : role === 'stakeholder'
                      ? 'stakeholder'
                      : 'external'
              }
              imageUrl={avatarImageUrl}
              className={cn('h-10 w-10 border border-border/60', isStacked && 'scale-95')}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          className="w-64"
          onPointerEnter={clearHoverTimer}
          onPointerLeave={closeCallout}
        >
          <div className="space-y-2 text-sm text-foreground">
            <div>
              <p className="text-sm font-semibold leading-tight">{name}</p>
              <p className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground/80">
                {roleTokens.label}
              </p>
            </div>
            {title ? <p className="text-muted-foreground">{title}</p> : null}
            {organization ? (
              <p className="text-muted-foreground/80">
                <span className="font-semibold text-foreground/90">Org:</span> {organization}
              </p>
            ) : null}
            {handle ? (
              <p className="text-muted-foreground/80">
                <span className="font-semibold text-foreground/90">Handle:</span> {handle}
              </p>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
      {isStacked ? (
        <span className="mt-1 h-4 w-px bg-border/60" aria-hidden />
      ) : null}
    </div>
  );
}
